import React from 'react';

import { usePageSpaceKey } from '@confluence/page-context';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { useSmartFolderAncestorSync } from './useSmartFolderAncestorSync.ts';
import { useIsSmartFoldersEnabled } from './useIsSmartFoldersEnabled.ts';

/**
 * Inner component that runs the sync hook. Kept separate so the ErrorBoundary
 * above it can catch any errors thrown during the hook's execution without
 * taking down the rest of the page layout.
 */
const SmartFolderAncestorSyncInner = ({
	contentId,
}: {
	contentId: string | null | undefined;
}): null => {
	useSmartFolderAncestorSync(contentId);
	return null;
};

/**
 * Mounts the `useSmartFolderAncestorSync` hook for the current content route
 * so that opening any deep link (page, blog post, etc.) inside a smart folder
 * triggers a sync of the root smart folder.
 *
 * Wrapped in an ErrorBoundary attributed to INTEGRATIONS so that any sync
 * errors are caught and reported without affecting the rest of the page layout.
 *
 * Renders nothing.
 */
export const SmartFolderAncestorSync = ({
	contentId,
}: {
	contentId: string | null | undefined;
}) => {
	const [spaceKey] = usePageSpaceKey();
	const { isSmartFoldersEnabled } = useIsSmartFoldersEnabled(spaceKey ?? '');

	if (!isSmartFoldersEnabled) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.INTEGRATIONS}>
			<SmartFolderAncestorSyncInner contentId={contentId} />
		</ErrorBoundary>
	);
};
